import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCollection, getId, getSiteLabel, requestJson } from '../lib/api';
import { useMenuPage } from '../hooks/useMenuPage';

function normalizeSearch(value) {
  return String(value || '').trim().toLowerCase();
}

function compareText(left, right) {
  return String(left || '').localeCompare(String(right || ''), 'fr', { numeric: true, sensitivity: 'base' });
}

function getSupplierAccountSiteLabel(site) {
  return site?.code || getSiteLabel(site) || '—';
}

export default function SupplierAccountsPage() {
  const menuPage = useMenuPage('/comptes-fournisseurs');
  const [sites, setSites] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [draftValues, setDraftValues] = useState({});
  const [search, setSearch] = useState('');
  const [storeFilter, setStoreFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'site', direction: 'asc' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [sitesPayload, suppliersPayload, accountsPayload] = await Promise.all([
        requestJson('/sites?limit=500'),
        requestJson('/suppliers'),
        requestJson('/supplier-accounts'),
      ]);
      setSites(getCollection(sitesPayload).filter((site) => site.type === 'store' && site.active !== false).sort((a, b) => compareText(getSupplierAccountSiteLabel(a), getSupplierAccountSiteLabel(b))));
      setSuppliers(getCollection(suppliersPayload).filter((supplier) => supplier.active !== false).sort((a, b) => compareText(a.name, b.name)));
      setAccounts(getCollection(accountsPayload));
    } catch (err) {
      setError(err.message);
      setSites([]);
      setSuppliers([]);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const accountMap = useMemo(() => {
    const map = new Map();
    accounts.forEach((account) => {
      map.set(`${getId(account.siteId)}:${getId(account.supplierId)}`, account);
    });
    return map;
  }, [accounts]);

  const buildDraftValues = useCallback(() => {
    const next = {};
    accounts.forEach((account) => {
      next[`${getId(account.siteId)}:${getId(account.supplierId)}`] = account.accountNumber || '';
    });
    return next;
  }, [accounts]);

  const visibleSuppliers = useMemo(
    () => suppliers.filter((supplier) => !supplierFilter || supplier._id === supplierFilter),
    [supplierFilter, suppliers]
  );

  useEffect(() => {
    if (sortConfig.key !== 'site' && supplierFilter && sortConfig.key !== supplierFilter) {
      setSortConfig({ key: supplierFilter, direction: 'asc' });
    }
  }, [sortConfig.key, supplierFilter]);

  const filteredSites = useMemo(() => {
    const needle = normalizeSearch(search);
    const normalizedStoreFilter = normalizeSearch(storeFilter);
    return sites.filter((site) => {
      const values = [site.code, getSupplierAccountSiteLabel(site)];
      suppliers.forEach((supplier) => {
        const account = accountMap.get(`${site._id}:${supplier._id}`);
        if (account?.accountNumber) values.push(supplier.name, account.accountNumber);
      });
      const haystack = normalizeSearch(values.join(' '));
      return (!needle || haystack.includes(needle)) && (!normalizedStoreFilter || normalizeSearch(getSupplierAccountSiteLabel(site)).includes(normalizedStoreFilter));
    });
  }, [accountMap, search, sites, storeFilter, suppliers]);

  const sortedSites = useMemo(() => {
    const sorted = [...filteredSites];
    sorted.sort((left, right) => {
      let compare = 0;
      if (sortConfig.key === 'site') {
        compare = compareText(getSupplierAccountSiteLabel(left), getSupplierAccountSiteLabel(right));
      } else {
        const leftAccount = accountMap.get(`${left._id}:${sortConfig.key}`)?.accountNumber || '';
        const rightAccount = accountMap.get(`${right._id}:${sortConfig.key}`)?.accountNumber || '';
        compare = compareText(leftAccount, rightAccount);
      }
      return sortConfig.direction === 'asc' ? compare : -compare;
    });
    return sorted;
  }, [accountMap, filteredSites, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const startEditMode = () => {
    setDraftValues(buildDraftValues());
    setEditMode(true);
    setMessage('');
    setError('');
  };

  const cancelEditMode = () => {
    setDraftValues({});
    setEditMode(false);
  };

  const setDraftValue = (siteId, supplierId, value) => {
    setDraftValues((current) => ({
      ...current,
      [`${siteId}:${supplierId}`]: value,
    }));
  };

  const saveMatrix = async () => {
    try {
      setSaving(true);
      setError('');
      setMessage('');
      let createdOrUpdated = 0;
      let deleted = 0;

      for (const site of sites) {
        for (const supplier of suppliers) {
          const key = `${site._id}:${supplier._id}`;
          const existing = accountMap.get(key);
          const initialValue = existing?.accountNumber || '';
          const nextValue = String(draftValues[key] ?? '').trim();
          if (nextValue === initialValue) continue;

          if (nextValue) {
            await requestJson('/supplier-accounts', {
              method: 'POST',
              body: JSON.stringify({
                siteId: site._id,
                supplierId: supplier._id,
                accountNumber: nextValue,
                note: existing?.note || '',
              }),
            });
            createdOrUpdated += 1;
          } else if (existing?._id) {
            await requestJson(`/supplier-accounts/${existing._id}`, { method: 'DELETE' });
            deleted += 1;
          }
        }
      }

      await loadData();
      setDraftValues({});
      setEditMode(false);
      setMessage(`${createdOrUpdated} compte(s) enregistré(s), ${deleted} suppression(s).`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 900 }}>
            {menuPage.sectionTitle}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            Comptes fournisseurs
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={loadData} disabled={saving}>
            Rafraîchir
          </Button>
          {editMode ? (
            <>
              <Button variant="outlined" onClick={cancelEditMode} disabled={saving}>
                Annuler
              </Button>
              <Button variant="contained" startIcon={<SaveRoundedIcon />} onClick={saveMatrix} disabled={saving}>
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </>
          ) : (
            <Button variant="contained" startIcon={<EditRoundedIcon />} onClick={startEditMode} disabled={loading}>
              Mode édition
            </Button>
          )}
        </Stack>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  Tableau des comptes
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {sortedSites.length} magasin{sortedSites.length > 1 ? 's' : ''} · {visibleSuppliers.length} fournisseur{visibleSuppliers.length > 1 ? 's' : ''}
                </Typography>
              </Box>
              <TextField
                label="Magasin"
                value={storeFilter}
                onChange={(event) => setStoreFilter(event.target.value)}
                sx={{ minWidth: { xs: '100%', md: 180 } }}
              />
              <TextField
                select
                label="Fournisseur"
                value={supplierFilter}
                onChange={(event) => setSupplierFilter(event.target.value)}
                sx={{ minWidth: { xs: '100%', md: 220 } }}
              >
                <MenuItem value="">Tous</MenuItem>
                {suppliers.map((supplier) => (
                  <MenuItem key={supplier._id} value={supplier._id}>
                    {supplier.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Recherche"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{ minWidth: { xs: '100%', md: 320 } }}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  setSearch('');
                  setStoreFilter('');
                  setSupplierFilter('');
                  setSortConfig({ key: 'site', direction: 'asc' });
                }}
              >
                Réinitialiser
              </Button>
            </Stack>

            {loading ? (
              <Stack alignItems="center" sx={{ py: 8 }}>
                <CircularProgress />
              </Stack>
            ) : (
              <TableContainer sx={{ maxHeight: 620, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 3,
                          bgcolor: 'background.paper',
                          minWidth: 220,
                          fontWeight: 900,
                        }}
                      >
                        <TableSortLabel
                          active={sortConfig.key === 'site'}
                          direction={sortConfig.key === 'site' ? sortConfig.direction : 'asc'}
                          onClick={() => handleSort('site')}
                        >
                          Magasin
                        </TableSortLabel>
                      </TableCell>
                      {visibleSuppliers.map((supplier) => (
                        <TableCell key={supplier._id} align="center" sx={{ minWidth: 150, fontWeight: 900 }}>
                          <TableSortLabel
                            active={sortConfig.key === supplier._id}
                            direction={sortConfig.key === supplier._id ? sortConfig.direction : 'asc'}
                            onClick={() => handleSort(supplier._id)}
                          >
                            {supplier.name}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedSites.map((site) => (
                      <TableRow key={site._id} hover>
                        <TableCell
                          sx={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 2,
                            bgcolor: 'background.paper',
                            fontWeight: 900,
                            borderRight: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          {getSupplierAccountSiteLabel(site)}
                        </TableCell>
                        {visibleSuppliers.map((supplier) => {
                          const account = accountMap.get(`${site._id}:${supplier._id}`);
                          const draftKey = `${site._id}:${supplier._id}`;
                          return (
                            <TableCell key={supplier._id} align="center">
                              {editMode ? (
                                <TextField
                                  size="small"
                                  value={draftValues[draftKey] ?? ''}
                                  onChange={(event) => setDraftValue(site._id, supplier._id, event.target.value)}
                                  placeholder="Compte"
                                  disabled={saving}
                                  inputProps={{ style: { textAlign: 'center', fontWeight: 800 } }}
                                  sx={{
                                    minWidth: 120,
                                    '& .MuiInputBase-root': {
                                      height: 34,
                                    },
                                  }}
                                />
                              ) : (
                                <Typography
                                  variant="body2"
                                  color={account?.accountNumber ? 'text.primary' : 'text.secondary'}
                                  sx={{ fontWeight: account?.accountNumber ? 850 : 500, minHeight: 28, display: 'inline-flex', alignItems: 'center' }}
                                >
                                  {account?.accountNumber || '—'}
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                    {!sortedSites.length && (
                      <TableRow>
                        <TableCell colSpan={visibleSuppliers.length + 1}>
                          <Alert severity="info">Aucun magasin trouvé.</Alert>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </CardContent>
      </Card>

    </Stack>
  );
}
